/**
 * P2-12 · The PLP sort URL contract. A small fixed option set mapped to the
 * P2-10 sort keys; the brand config picks the default and (optionally) trims
 * the enabled list. The default sort is dropped from the URL for canonical
 * links, and any change resets to page 1.
 */
import { listingSortSchema, type ListingSort } from "~/server/api/schemas/listing";

/** The fixed, ordered sort options (labels are storefront copy). */
export const SORT_OPTIONS: { key: ListingSort; label: string }[] = [
  { key: "relevance", label: "Relevance" },
  { key: "newest", label: "Newest" },
  { key: "price-asc", label: "Price: low to high" },
  { key: "price-desc", label: "Price: high to low" },
  { key: "best-selling", label: "Best selling" },
  { key: "rating", label: "Top rated" },
];

export const DEFAULT_SORT: ListingSort = "relevance";

export interface SortConfig {
  defaultSort?: ListingSort;
  sortOptions?: ListingSort[];
}

/** The brand's default sort, falling back to the platform default. */
export function resolveDefaultSort(config?: SortConfig): ListingSort {
  return config?.defaultSort ?? DEFAULT_SORT;
}

/** The enabled options for a brand (all when unconfigured); default always kept. */
export function enabledSortOptions(
  config?: SortConfig,
): { key: ListingSort; label: string }[] {
  const enabled = config?.sortOptions;
  if (!enabled?.length) return SORT_OPTIONS;
  const set = new Set<ListingSort>([...enabled, resolveDefaultSort(config)]);
  return SORT_OPTIONS.filter((o) => set.has(o.key));
}

/**
 * The sort the `sort=` param names, or null when it names none.
 *
 * P15-19 · Split out of {@link parseSortParam} because the PLP now needs to
 * tell "the shopper chose this" from "we fell back to it" — and deriving that
 * by comparing a fallback result against the fallback value is the kind of
 * reasoning that quietly breaks the day someone chooses the default
 * explicitly. One normalisation, one answer; `parseSortParam` is this plus a
 * `??`.
 */
export function sortParamOrNull(raw: string | undefined): ListingSort | null {
  const parsed = listingSortSchema.safeParse(raw?.replace(/_/g, "-"));
  return parsed.success ? parsed.data : null;
}

/** Parse the `sort=` param (hyphen or underscore), falling back to the default. */
export function parseSortParam(
  raw: string | undefined,
  def: ListingSort,
): ListingSort {
  return sortParamOrNull(raw) ?? def;
}

/** Write a sort selection to the query, dropping the default and resetting page. */
export function setSortParam(
  search: string,
  key: ListingSort,
  def: ListingSort,
): string {
  const params = new URLSearchParams(search);
  if (key === def) params.delete("sort");
  else params.set("sort", key);
  params.delete("page"); // a sort change returns to the first page
  return params.toString();
}
